<script setup lang="ts">
  import { computed, ref } from 'vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import {
    type CustomerCase as Case,
    type CustomerEntity,
    type CustomerEntityWithRelationship as EntityWithRelationship,
    EntityTypeEnum,
    RelationshipTypeEnum
  } from '@/types/generated'
  import LegacyEntityForm from './LegacyEntityForm.vue'
  import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
  } from '@/components/ui/alert-dialog'
  import { Button } from '@/components/ui/button'
  import { useToast } from '@/components/ui/toast'
  import { formatEntityName as formatEntityNameUtil } from '@/utils/entityFormatting'
  import { Minus, Trash2, UserPlus } from 'lucide-vue-next'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'

  const { t } = useTranslation({
    en: {
      opponent: 'Opponent',
      addOpponent: 'Add Opponent',
      delete: 'Delete',
      confirmDelete: 'Confirm deletion',
      confirmDeleteMessage:
        'Are you sure you want to delete this opponent? This action cannot be undone.',
      confirmBulkDeleteMessage: 'Are you sure you want to delete the following opponents?',
      cancel: 'Cancel',
      deleteError: 'Error',
      deleteErrorMessage: 'An error occurred while deleting the opponent. Please try again.',
      addError: 'Error',
      addErrorMessage: 'An error occurred while adding the opponent. Please try again.',
      unnamed: 'Unnamed',
      selected: 'selected',
      deleteSelected: 'Delete selected'
    },
    de: {
      opponent: 'Gegner',
      addOpponent: 'Gegner hinzufügen',
      delete: 'Löschen',
      confirmDelete: 'Löschen bestätigen',
      confirmDeleteMessage:
        'Sind Sie sicher, dass Sie diesen Gegner löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.',
      confirmBulkDeleteMessage: 'Sind Sie sicher, dass Sie die folgenden Gegner löschen möchten?',
      cancel: 'Abbrechen',
      deleteError: 'Fehler',
      deleteErrorMessage:
        'Beim Löschen des Gegners ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
      addError: 'Fehler',
      addErrorMessage:
        'Beim Hinzufügen des Gegners ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
      unnamed: 'Unbenannt',
      selected: 'ausgewählt',
      deleteSelected: 'Ausgewählte löschen'
    }
  })

  const { toast } = useToast()
  const caseStore = useCaseStore()
  const caseRecord = computed<Case | null>(() => caseStore.caseRecord)

  const opponentEntities = computed(() => {
    if (!caseRecord.value?.data?.entities) return []
    return caseRecord.value.data.entities.filter((entity) =>
      entity.relationships.some(
        (r) => r.relationship_type === RelationshipTypeEnum.ENTITIES_OPPONENT
      )
    )
  })

  const expandedCards = ref<Set<string>>(new Set())
  const selectedOpponents = ref<Set<string>>(new Set())
  const showDeleteConfirmation = ref(false)
  const opponentToDelete = ref<EntityWithRelationship | null>(null)
  const isBulkDelete = ref(false)

  const formatEntityName = (entity: CustomerEntity | null | undefined): string => {
    return formatEntityNameUtil(entity, t('unnamed'))
  }

  const hasSelection = computed(() => selectedOpponents.value.size > 0)

  const opponentsToDeleteList = computed<EntityWithRelationship[]>(() => {
    if (!isBulkDelete.value) {
      return opponentToDelete.value ? [opponentToDelete.value] : []
    }
    return opponentEntities.value.filter((o) => o.uuid && selectedOpponents.value.has(o.uuid))
  })

  const toggleSelection = (uuid: string) => {
    if (selectedOpponents.value.has(uuid)) {
      selectedOpponents.value.delete(uuid)
    } else {
      selectedOpponents.value.add(uuid)
    }
  }

  const deselectAll = () => {
    selectedOpponents.value.clear()
  }

  const toggleCard = (entityUuid: string) => {
    if (expandedCards.value.has(entityUuid)) {
      expandedCards.value.delete(entityUuid)
    } else {
      expandedCards.value.add(entityUuid)
    }
  }

  const confirmDeleteOpponent = (opponent: EntityWithRelationship) => {
    opponentToDelete.value = opponent
    isBulkDelete.value = false
    showDeleteConfirmation.value = true
  }

  const confirmBulkDelete = () => {
    if (selectedOpponents.value.size === 0) return
    opponentToDelete.value = null
    isBulkDelete.value = true
    showDeleteConfirmation.value = true
  }

  const deleteOpponents = async () => {
    try {
      const opponents = opponentsToDeleteList.value
      if (opponents.length === 1) {
        await caseStore.deleteEntity(opponents[0])
      } else if (opponents.length > 1) {
        await caseStore.bulkDeleteEntities(opponents)
      }
      for (const o of opponents) {
        if (o.uuid) expandedCards.value.delete(o.uuid)
      }
      showDeleteConfirmation.value = false
      opponentToDelete.value = null
      selectedOpponents.value.clear()
      isBulkDelete.value = false
    } catch (error) {
      console.error('Error deleting opponent:', error)
      toast({
        title: t('deleteError'),
        description: t('deleteErrorMessage'),
        variant: 'destructive'
      })
    }
  }

  const addOpponent = async () => {
    const newEntity: CustomerEntity = {
      uuid: '',
      entity_type: EntityTypeEnum.NATURAL_PERSON,
      title: '',
      specific_title: '',
      first_name: '',
      last_name: '',
      company_name: '',
      company_legal_form: '',
      institution_name: '',
      phone: '',
      email: '',
      date_of_birth: null,
      street: '',
      street_number: '',
      address_line_2: '',
      zip: '',
      city: '',
      legal_insurance: null,
      legal_insurance_number: '',
      legal_insurance_name: '',
      is_company: 'False',
      changes: [],
      proposed_changes: []
    }

    try {
      const newEntityRelationship = await caseStore.addEntity(
        newEntity,
        RelationshipTypeEnum.ENTITIES_OPPONENT
      )

      if (newEntityRelationship?.uuid) {
        expandedCards.value.add(newEntityRelationship.uuid)
      }
    } catch (error) {
      console.error('Error adding opponent:', error)
      toast({
        title: t('addError'),
        description: t('addErrorMessage'),
        variant: 'destructive'
      })
    }
  }
</script>

<template>
  <div class="relative">
    <!-- Bulk action banner -->
    <div
      v-if="hasSelection"
      class="mb-3 flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2
        shadow-sm"
    >
      <div class="flex items-center gap-2">
        <Button
          size="sm"
          class="h-5 w-5 rounded-sm border border-iris-600 bg-iris-600 p-0"
          @click="deselectAll"
        >
          <Minus class="h-4 w-4 text-white" />
        </Button>
        <span class="text-sm font-medium text-gray-700">
          {{ selectedOpponents.size }} {{ t('selected') }}
        </span>
      </div>
      <Button
        variant="ghost"
        size="sm"
        class="text-red-600 hover:bg-red-50 hover:text-red-600"
        @click="confirmBulkDelete"
      >
        <Trash2 class="mr-2 h-4 w-4" />
        {{ t('deleteSelected') }}
      </Button>
    </div>

    <!-- Opponent Cards -->
    <template
      v-for="(opponent, index) in opponentEntities"
      :key="opponent.uuid"
    >
      <div
        v-if="opponent.uuid"
        :class="{ 'mt-3': index > 0 }"
      >
        <div
          class="card"
          :data-cy="`opponentCard-${index}`"
        >
          <h3
            :id="`heading-opponent-${index}`"
            class="accordion-button mb-0 h-16 bg-white"
            :class="{ collapsed: !expandedCards.has(opponent.uuid) }"
            :data-cy="`opponentCardHeader-${index}`"
            @click="toggleCard(opponent.uuid)"
          >
            <div class="flex w-full items-center justify-between">
              <div class="flex items-center gap-2">
                <CheckboxField
                  :checked-value="true"
                  :unchecked-value="false"
                  :model-value="selectedOpponents.has(opponent.uuid)"
                  :name="`opponent-selection-${opponent.uuid}`"
                  @click.stop
                  @update:model-value="toggleSelection(opponent.uuid)"
                />
                <div class="flex flex-col">
                  <span class="text-base font-medium text-neutral-800">{{ t('opponent') }}</span>
                  <span
                    v-if="!expandedCards.has(opponent.uuid)"
                    class="max-w-64 truncate text-sm font-medium text-neutral-500"
                  >
                    {{ formatEntityName(opponent.entity) }}
                  </span>
                </div>
              </div>

              <div class="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  class="mr-2 h-8 w-8 p-0"
                  :data-cy="`deleteOpponent-${index}`"
                  @click.stop="confirmDeleteOpponent(opponent)"
                >
                  <Trash2 class="h-5 w-5" />
                </Button>
              </div>
            </div>
          </h3>

          <div
            v-if="expandedCards.has(opponent.uuid)"
            :id="`opponentCard-${index}`"
            class="card-body"
            :aria-labelledby="`heading-opponent-${index}`"
          >
            <LegacyEntityForm :entity-relationship-uuid="opponent.uuid" />
          </div>
        </div>
      </div>
    </template>

    <!-- Add Opponent Button -->
    <div class="mt-3">
      <Button
        variant="secondary"
        class="w-full"
        data-cy="addOpponentButton"
        @click="addOpponent"
      >
        <UserPlus class="mr-2 h-5 w-5" />
        {{ t('addOpponent') }}
      </Button>
    </div>

    <!-- Delete Confirmation Modal -->
    <AlertDialog
      :open="showDeleteConfirmation"
      @update:open="(val) => (showDeleteConfirmation = val)"
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{{ t('confirmDelete') }}</AlertDialogTitle>
          <AlertDialogDescription>
            <template v-if="isBulkDelete">
              {{ t('confirmBulkDeleteMessage') }}
              <ul class="mt-2 list-disc pl-5">
                <li
                  v-for="opponent in opponentsToDeleteList"
                  :key="opponent.uuid"
                  class="font-medium"
                >
                  {{ formatEntityName(opponent.entity) }}
                </li>
              </ul>
            </template>
            <template v-else>
              {{ t('confirmDeleteMessage') }}
            </template>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            @click="
              () => {
                showDeleteConfirmation = false
                opponentToDelete = null
                isBulkDelete = false
              }
            "
          >
            {{ t('cancel') }}
          </AlertDialogCancel>
          <AlertDialogAction
            class="bg-red-600 hover:bg-red-700"
            @click="deleteOpponents"
          >
            {{ t('delete') }}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>
</template>
